import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { Panel } from 'primeng/panel';
import { OrderList } from 'primeng/orderlist';
import { HisCoreSharedModule } from '../../../../core/his-core-shared.module';
import { HisAuthService } from '../../../../auth/service/his-auth.service';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { FormsModule } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { cloneDeep } from 'lodash';
import { StoredReport } from '../../../model/stored-report';
import { BiAnalysisService } from '../../../services/bi-analysis.service';
import { SmartTreeTableComponent } from '../bi-analysis-table/smart-tree-table/component/smart-treetable.component';
import { Result } from '../bi-analysis-table/flat-table/model/result';
import { ResultType } from '../bi-analysis-table/flat-table/model/result-type';
import { SaveBiAnalysisComponent } from './save-bi-analysis/save-bi-analysis.component';
import { CreatePermalinkComponent } from './create-permalink/create-permalink.component';
import { take } from 'rxjs';
import { HisNotificationService } from '../../../../core/service/notifications/his-notification.service';
import { HisUiService } from '../../../../core/service/base/his-ui.service';

@Component( {
    selector: 'his-bi-analysis-action-bar',
    imports: [
        Panel,
        SaveBiAnalysisComponent,
        CreatePermalinkComponent,
        HisCoreSharedModule,
        OverlayPanelModule,
        OrderList,
        FormsModule
    ],

    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './bi-analysis-action-bar.component.html'
} )
export class BiAnalysisActionBarComponent {

    // Needed for download
    @Input( { required: true } ) report!: StoredReport;
    @Input( { required: true } ) drillDownTableResult!: Result;
    @Input( { required: true } ) flatTableResult!: Result;
    @Input() smartTreeTable?: SmartTreeTableComponent;
    @Input() enableReportSharing?: boolean = false;

    biAnalysisService = inject( BiAnalysisService );
    hisUi = inject( HisUiService );

    // Needed for Save Report
    @Input() edit: boolean = false;
    @Output() readonly reportChanged = new EventEmitter<StoredReport>();


    // Needed for switchable Table
    @Output() readonly tableTypeChanged = new EventEmitter<ResultType>;
    currentTableType: ResultType = ResultType.DrilldownGroupable;
    tableTypeText = 'cs.bia.analysis.flatTable';

    // Needed for overlay
    @Input() headerOrder: string[] = [];  // is this really needed? we already have drillDownTable.headerOrder here?
    @Output() readonly headerOrderChanged = new EventEmitter<string[]>;
    @Output() readonly totalColumnChanged = new EventEmitter<number>;

    protected hisAuthService = inject( HisAuthService );
    notificationService = inject( HisNotificationService );

    download( hierarchy?: boolean ) {

        if ( !this.report ) {
            throw new Error( 'Cannot download report. Object does not exist.' );
        }

        this.report.exportedResult = this.getExportableResult( hierarchy );

        if ( this.smartTreeTable?.treeResult ) {
            this.report.hierarchy = this.setHierarchyWithoutParents( this.smartTreeTable.treeResult );
        }

        if ( hierarchy ) {
            this.report.exportedResult!.resultType = ResultType.DrilldownGroupable;
        } else {
            this.report.exportedResult!.resultType = ResultType.FlatTable;
        }

        this.biAnalysisService.downloadExcelFile( this.report )
            .pipe( take( 1 ) )
            .subscribe(
                ( element: HTMLAnchorElement ) => {
                    document.body.appendChild( element );
                    element.click();
                    document.body.removeChild( element );
                },
            );
    }

    getExportableResult( hierarchy?: boolean ): Result | undefined {
        if ( hierarchy ) {
            return this.drillDownTableResult;
        } else {

            return this.flatTableResult;
        }
    }

    private removeParentAttribute( treeResult: TreeNode<any>[] ) {
        for ( let node of treeResult ) {
            if ( node.parent ) {
                node.parent = undefined;
            }
            if ( node.children ) {
                this.removeParentAttribute( node.children );
            }
        }
    }

    setHierarchyWithoutParents( treeResult: TreeNode<any>[] ) {
        let cloneResult = cloneDeep( treeResult );
        this.removeParentAttribute( cloneResult );
        return cloneResult;
    }

    setTotalColumn() {
        this.totalColumnChanged.emit( this.report.showTotalColumn );
    }

    rearrangeHeader() {
        this.headerOrderChanged.emit( this.headerOrder );
    }

    onSaveReport( $event: StoredReport ) {
        this.reportChanged.emit( $event );
    }


    switchTable() {
        if ( this.currentTableType !== ResultType.DrilldownGroupable ) {
            this.tableTypeText = 'cs.bia.analysis.flatTable';
            this.currentTableType = ResultType.DrilldownGroupable;
            this.tableTypeChanged.emit( ResultType.DrilldownGroupable );
        } else {
            this.tableTypeText = 'cs.bia.analysis.hierarchicalTable';
            this.tableTypeChanged.emit( ResultType.FlatTable );
            this.currentTableType = ResultType.FlatTable;
        }
    }

    downloadJR() {
        if ( this.report === undefined || this.report?.id === null ) {
            throw new Error( 'Cannot download report. Object does not exist.' );
        }

        this.biAnalysisService.downloadJasperReport( this.report.id )
            .pipe( take( 1 ) )
            .subscribe(
                ( element: HTMLAnchorElement ) => {
                    document.body.appendChild( element );
                    element.click();
                    document.body.removeChild( element );
                },
            );
    }

    downloadJson(): void {
        const date = new Date();
        const timestamp = [
            String( date.getFullYear() ),
            String( date.getMonth() + 1 ).padStart( 2, '0' ),
            String( date.getDate() ).padStart( 2, '0' ),
            '_',
            String( date.getHours() ).padStart( 2, '0' ),
            String( date.getMinutes() ).padStart( 2, '0' ),
            String( date.getSeconds() ).padStart( 2, '0' ),
        ].join( '' );
        const reportName = this.report.name?.trim();
        let fileName = this.report.id && reportName ? reportName : 'bianalysis_definition';
        fileName = fileName.replace( /[^a-zA-Z0-9äöüÄÖÜß_\-]+/g, '_' );
        if ( fileName.length > 206 ) {
            fileName = fileName.substring( 0, 160 );
        }

        const reportToCopy = {
            id: this.report.id,
            name: this.report.name,
            description: this.report.description,
            reportDefinition: {
                ...this.report.reportDefinition,
                filters: this.report.reportDefinition.filters.map( filter => ( {
                    dimensionAttributeId: filter.dimensionAttributeId,
                    filterValues: filter.filterValues
                } ) )
            }
        };
        const blob = new Blob(
            [JSON.stringify( reportToCopy, null, 2 )],
            { type: 'application/json' }
        );

        const url = URL.createObjectURL( blob );
        const link = document.createElement( 'a' );

        link.href = url;
        link.download = `${fileName}_${timestamp}.json`;
        link.click();

        URL.revokeObjectURL( url );
    }
}
